import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MessageSquare,
  Mic,
  Volume2,
  MemoryStick,
  Cpu,
  ExternalLink,
  FileText,
  CheckCircle2,
  XCircle,
  Sparkles,
  Copy,
  Check,
  Database,
  Layers,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { NeMoPretrainedModel, NeMoTaskType, getTrainingMethodLabel } from '@/mock/gpu-instance';
import { useState } from 'react';

interface NeMoModelCardProps {
  model: NeMoPretrainedModel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectModel?: (model: NeMoPretrainedModel) => void;
}

const getTaskInfo = (task: NeMoTaskType) => {
  switch (task) {
    case 'llm':
      return {
        name: 'Language Model',
        icon: MessageSquare,
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
      };
    case 'asr':
      return {
        name: 'Speech Recognition',
        icon: Mic,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
      };
    case 'tts':
      return {
        name: 'Text-to-Speech',
        icon: Volume2,
        color: 'text-purple-500',
        bgColor: 'bg-purple-500/10',
      };
  }
};

const getOutputClassContext = (task: NeMoTaskType): string => {
  switch (task) {
    case 'llm':
      return 'Vocabulary tokens for text generation';
    case 'asr':
      return 'Character/phoneme classes for transcription';
    case 'tts':
      return 'Output features for speech synthesis';
  }
};

const NeMoModelCard = ({
  model,
  open,
  onOpenChange,
  onSelectModel,
}: NeMoModelCardProps) => {
  const [copiedPath, setCopiedPath] = useState(false);

  if (!model) return null;

  const taskInfo = getTaskInfo(model.task);
  const TaskIcon = taskInfo.icon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(model.ngcPath);
      setCopiedPath(true);
      setTimeout(() => setCopiedPath(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-blue-500/10`}>
              <TaskIcon className={`h-8 w-8 ${taskInfo.color}`} />
            </div>
            <div>
              <DialogTitle className="text-xl">{model.displayName}</DialogTitle>
              <DialogDescription className="mt-1">{model.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Task Badge */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Badge className={`${taskInfo.bgColor} ${taskInfo.color} border-current/30`}>
            <TaskIcon className="h-3 w-3 mr-1" />
            {taskInfo.name}
          </Badge>
          <Badge variant="outline" className="font-mono text-xs">
            {model.parameters} params
          </Badge>
          <Badge variant="secondary">{model.family}</Badge>
        </div>

        <Separator className="my-4" />

        {/* Key Metrics */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Cpu className="h-4 w-4 text-blue-500" />
            Performance Metrics
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Parameters</div>
                <div className="text-2xl font-bold">{model.parameters}</div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <MemoryStick className="h-3 w-3" />
                  VRAM (Full)
                </div>
                <div className="text-2xl font-bold">{model.vramRequired} GB</div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <Sparkles className="h-3 w-3 text-green-500" />
                  VRAM (LoRA)
                </div>
                <div className="text-2xl font-bold text-green-600">{model.vramLoRA} GB</div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">
                  {model.task === 'llm' ? 'Context Length' : 'Languages'}
                </div>
                <div className="text-xl font-bold font-mono">
                  {model.contextLength
                    ? `${(model.contextLength / 1000).toFixed(0)}K`
                    : model.languages?.join(', ').toUpperCase() || '-'}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Pretrained Information */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Database className="h-4 w-4 text-blue-500" />
            Pretrained Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-muted/30">
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground mb-1">Pretrained Dataset</div>
                <div className="font-medium">{model.pretrainedDataset}</div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground mb-1">Output Classes</div>
                <div className="font-medium text-lg">
                  {model.outputClasses.toLocaleString()}
                  <span className="text-xs text-muted-foreground ml-1">classes</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {getOutputClassContext(model.task)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Prediction Classes / Capabilities */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Layers className="h-4 w-4 text-purple-500" />
            {model.task === 'llm' ? 'Model Capabilities' : 'Output Classes'} ({model.outputClassNames.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {model.outputClassNames.slice(0, 15).map((className, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {className}
              </Badge>
            ))}
            {model.outputClassNames.length > 15 && (
              <Badge variant="outline" className="text-xs">
                +{model.outputClassNames.length - 15} more
              </Badge>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        {/* NGC Path */}
        <div>
          <h3 className="text-sm font-semibold mb-3">NGC Path</h3>
          <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
            <code className="text-sm font-mono truncate">{model.ngcPath}</code>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 ml-2"
              onClick={handleCopy}
            >
              {copiedPath ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
            </Button>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Supported Methods */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Supported Training Methods</h3>
          <div className="flex flex-wrap gap-2">
            {model.supportedMethods.map((method) => (
              <Badge key={method} variant="outline" className="text-xs font-mono">
                {getTrainingMethodLabel(method)}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Use Cases */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            Recommended Use Cases
          </h3>
          <div className="flex flex-wrap gap-2">
            {model.useCases.map((useCase, idx) => (
              <Badge key={idx} variant="secondary" className="text-sm">
                {useCase}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Strengths & Limitations */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Strengths
            </h3>
            <ul className="space-y-2">
              {model.strengths.map((strength, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <XCircle className="h-4 w-4 text-yellow-500" />
              Limitations
            </h3>
            <ul className="space-y-2">
              {model.limitations.map((limitation, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                  <span>{limitation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-4" />

        {/* License */}
        <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
          <span className="text-sm text-muted-foreground">License</span>
          <Badge variant="outline" className="text-xs">
            {model.license}
          </Badge>
        </div>

        {/* Actions */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          {model.paperReference && (
            <Button
              variant="outline"
              onClick={() => window.open(model.paperReference, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Paper
            </Button>
          )}
          {!model.paperReference && <div />}
          {onSelectModel && (
            <Button onClick={() => {
              onSelectModel(model);
              onOpenChange(false);
            }} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Select Model
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NeMoModelCard;
